<template>
  <BaseBody>
    <BaseTopBar>
      <BaseTopBarContent className="left pr-0">
        {{ $t('sidebar.serialnumber') }} > {{ serialSingle.serCode }}
      </BaseTopBarContent>
      <BaseTopBarContent className="right pl-0">
        <div
          v-if="!loading"
          class="action">
          <template v-if="device.isMobile">
            <BaseActionDropdown
              :icon="`fas fa-cog`"
              :text="$t('button.options')"
              :items="[
                {
                  icon: 'fas fa-copy',
                  iconClassName: '',
                  text: $t('button.clone'),
                  textClassName: '',
                  value: 'clone',
                },
                {
                  icon: 'fas fa-trash',
                  iconClassName: 'text-danger',
                  text: $t('button.delete'),
                  textClassName: 'text-danger',
                  value: 'delete',
                }
              ]"
              @onSelect="handleSelectOption"
            />
          </template>
          <template v-else>
            <BaseActionDropdown
              v-if="hasRoleDelete"
              :icon="`fas fa-cog`"
              :text="$t('button.options')"
              :items="[
                {
                  icon: 'fas fa-trash',
                  iconClassName: 'text-danger',
                  text: $t('button.delete'),
                  textClassName: 'text-danger',
                  value: 'delete',
                }
              ]"
              @onSelect="handleSelectOption"
            />
            <BaseAction
              :icon="`fas fa-copy`"
              :iconClassName="``"
              :text="$t('button.clone')"
              :textClassName="``"
              @onClick="openCloneModal"
            />
          </template>
          <template>
            <div
              v-if="loadingStatus"
              class="dropdown nodro item">
              <i class="fas fa-spinner spin"/>
            </div>
            <template v-else>
              <BaseActionDropdown
                v-if="hasRoleEdit"
                :className="statusColor"
                :text="statusText"
                :items="[
                  {
                    text: $t('apps.inventory.message.serialready'),
                    textClassName: 'text-success',
                    value: true,
                    disabled: isEmpty,
                  },
                  {
                    text: $t('apps.inventory.message.serialnotready'),
                    textClassName: 'text-warning',
                    value: false,
                    disabled: isEmpty,
                  }
                ]"
                @onSelect="handleSelectStatus"
              />
              <div
                v-if="!hasRoleEdit"
                class="dropdown item">
                <span
                  :class="[statusColor]">
                  {{ statusText }}
                </span>
              </div>
            </template>
          </template>
        </div>
      </BaseTopBarContent>
    </BaseTopBar>
    <BaseContent :loading="loading">
      <WrapperDoc>
        <template #form>
          <ImsSerialNumberForm
            :state="serialSingle"
            :serCode="serialSingle.serCode"
            :isSubmitted="isSubmitted"
            :warehouses="warehouses"
            :products="products"
            :disabled="disabledForm"
            :form.sync="form"
            :showActionButtons="!disabledForm"
            @onSubmit="handleSubmit"
          />

          <baseConfirmDeleteModal
            :show="modal.show"
            :loading="modal.loading"
            @onClose="handleCloseModal"
            @onCancel="handleCancleModal"
            @onSubmit="handleSumitModal"
          />

          <BaseConfirmDiscardModal
            :show="confirmDiscardModal.show"
            @onCancel="handleDiscardCancleModal"
            @onConfirm="handleDiscardConfirmModal"
          />

          <BaseConfirmCloneModal
            :show="modalConfirmClone.show"
            :loading="modalConfirmClone.loading"
            @onClose="handleCloseCloneModal"
            @onCancel="handleCancleCloneModal"
            @onConfirm="handleConfirmCloneModal({
              payload: payloadClone,
              createApiFn: createSerial,
              redirectTo: 'InventorySerialList',
              refCode: 'ser_code',
            })"
          />
        </template>
        <template #ref>
          <DocRef
            :refDoc="`ser`"
            :refId="serialSingle.serId"
            :refCode="serialSingle.serCode"
            :showLog="getInventorySetting.log_topic.is_active"
            :showAttachFile="getInventorySetting.attach_file.is_active"
            :logTopics="getInventorySetting.log_topic.options"
            :isSubmitted="isSubmitted"
          />
        </template>
      </WrapperDoc>
    </BaseContent>
  </BaseBody>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { toISODateString } from '@/helpers'
import sidebarModule from './../../sidebarModule'
import submenuModule from '../submenuModule'
import discardFormMixin from '@/mixins/discardForm'
import userPermissionMixin from '@/mixins/userPermission'
import confirmCloneMixin from '@/mixins/confirmClone'
import WrapperDoc from '@/components/combine/WrapperDoc.vue'
import DocRef from '@/components/combine/DocRef.vue'
import BaseConfirmCloneModal from '@/components/base/BaseConfirmCloneModal'

export default {
  components: {
    WrapperDoc,
    DocRef,
    BaseConfirmCloneModal,
  },
  mixins: [discardFormMixin, userPermissionMixin, confirmCloneMixin],
  data () {
    return {
      loading: true,
      loadingStatus: false,
      isSubmitted: false,
      query: {
        id: this.$route.query.id,
        code: this.$route.query.code,
      },
      modal: {
        show: false,
        loading: false,
      },
      form: null,
    }
  },

  computed: {
    ...mapState('serial', {
      serialSingle: 'serialSingle',
    }),
    ...mapGetters({
      sidebar: 'getSidebar',
      warehouses: 'getWarehouses',
      products: 'getProducts',
      getProductInfoBySkuId: 'getProductInfoBySkuId',
      getInventorySetting: 'getInventorySetting',
    }),
    isEmpty () {
      return this.serialSingle.unitBalance <= 0
    },
    statusColor () {
      if (this.isEmpty) {
        return 'text-grey'
      }
      return this.serialSingle.isReady
        ? 'text-success'
        : 'text-warning'
    },
    statusText () {
      if (this.isEmpty) {
        return this.$t('apps.inventory.message.serialempty')
      }
      return this.serialSingle.isReady
        ? this.$t('apps.inventory.message.serialready')
        : this.$t('apps.inventory.message.serialnotready')
    },
    disabledForm () {
      return !this.hasRoleEdit
    },
    payloadClone() {
      const productInfo = this.getProductInfoBySkuId(this.serialSingle.skuId)
      const productId = productInfo.product_id
      const stockId = productInfo
        .prices.find((price) => price.sku_id === this.serialSingle.skuId)
        .stocks.find((stock) => stock.warehouse_id === this.serialSingle.whId)
        .stock_id
      return {
        'stock_id': stockId,
        'sku_id': this.serialSingle.skuId,
        'product_id': productId,
        'wh_id': this.serialSingle.whId,
        'bin_location_id': this.serialSingle.binLocationId,
        'gr_id': null,
        'po_id': null,
        'serial_number': this.serialSingle.serialNumber,
        'date_in': this.serialSingle.dateIn,
        'date_product': this.serialSingle.dateProduct,
        'date_expire': this.serialSingle.dateExpire,
        'cost': this.serialSingle.cost,
        'note': this.serialSingle.note,
      }
    },
  },

  watch: {
    warehouses () {
      sidebarModule.init(this.getObject(require('../sidebar.json')))
      sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
      sidebarModule.update(this.sidebar, 7, [{
        'name': this.query.code,
        'router': {
          'name': 'InventorySerialEdit',
          'query': {
            id: this.query.id,
            name: this.query.code,
          },
        },
        'activeClass': 'active',
      }], {
        sidebarAtIdx: 7,
        submenuAtIdx: 2,
      })
    },
  },

  created () {
    this.fetchData()
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.inventory.title'),
      current: {
        app_name: 'inventory',
        app_id: 3,
        module_id: 8,
      },
    })
    // set sidebar stockcontrols ui
    sidebarModule.init(this.getObject(require('../sidebar.json')))
    sidebarModule.update(this.sidebar, 1, submenuModule.getWarehouses(this.warehouses))
    sidebarModule.update(this.sidebar, 7, [{
      'name': this.query.code,
      'router': {
        'name': 'InventorySerialEdit',
        'query': {
          id: this.query.id,
          name: this.query.code,
        },
      },
      'activeClass': 'active',
    }], {
      sidebarAtIdx: 7,
      submenuAtIdx: 2,
    })
  },

  destroyed () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    ...mapActions('serial', {
      getSerialSingle: 'getSerialSingle',
      updateSerial: 'updateSerial',
      updateSerialStatus: 'updateSerialStatus',
      deleteSerial: 'deleteSerial',
      createSerial: 'createSerial',
    }),
    async fetchData () {
      this.loading = true
      await Promise.all([
        this.$store.dispatch('getWarehouses'),
        this.$store.dispatch('getProducts'),
        this.getSerialSingle(this.query.id),
      ])
      if (!this.serialSingle.serId) {
        this.notify(this.$vs, {
          text: this.$t('message.notfounddata'),
          color: 'warning',
          icon: 'warning',
        })
        this.$router.push({ name: 'InventorySerialList' })
      }
      this.loading = false
      this.showFinish(this.$Progress)
    },
    getFormCreate (form = {}) {
      const productInfo = this.getProductInfoBySkuId(form.skuId)
      const productId = productInfo.product_id
      const stockId = productInfo
        .prices.find((price) => price.sku_id === form.skuId)
        .stocks.find((stock) => stock.warehouse_id === form.whId)
        .stock_id
      return {
        'stock_id': stockId,
        'sku_id': form.skuId,
        'product_id': productId,
        'wh_id': form.whId,
        'bin_location_id': form.binId,
        'gr_id': null,
        'po_id': null,
        'serial_number': form.serialNumber,
        'date_in': toISODateString(form.dateIn),
        'date_product': toISODateString(form.dateProduct),
        'date_expire': toISODateString(form.dateExpire),
        'unit_init': form.unit,
        'cost': form.cost,
        'note': form.note,
      }
    },
    async handleSubmit (form = {}) {
      this.isSubmitted = true
      const data = this.getFormCreate(form)
      const responseUpdate = await this.updateSerial({ id: this.query.id, data })
      if (responseUpdate.success) {
        this.isSubmitted = false
        this.notify(this.$vs, {
          text: this.$t('message.hasbeenupdated', { msg: this.serialSingle.serCode }),
          color: 'success',
          icon: 'cloud',
        })
        this.fetchData()
      }
    },
    handleSelectOption (value) {
      if (value === 'delete') {
        this.onOpenModal()
      } else if (value === 'clone') {
        this.openCloneModal()
      }
    },
    async handleSelectStatus (value) {
      this.isSubmitted = true
      this.loadingStatus = true
      const data = { is_ready: value }
      const response = await this.updateSerialStatus({ id: this.query.id, data })
      this.isSubmitted = false
      this.loadingStatus = false
      this.fetchData()
    },
    onOpenModal () {
      this.modal.show = true
    },
    handleCloseModal () {
      this.modal.show = false
    },
    handleCancleModal () {
      this.modal.show = false
    },
    async handleSumitModal () {
      this.modal.loading = true
      const response = await this.deleteSerial(this.query.id)
      if (response.success) {
        this.notify(this.$vs, {
          text: this.$t('message.hasbeendeleted', { msg: this.serialSingle.serCode }),
          color: 'danger',
          icon: 'delete',
        })
        this.modal.loading = false
        this.modal.show = false
        this.$router.push({ name: 'InventorySerialList' })
      }
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/apps/inventory/serialnumber/Edit.vue